import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, ArrowRight, ChevronLeft, ChevronRight, X } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { useCmsImages } from '../context/CmsImagesContext';

const BeforeAfterSlider = ({ before, after, title }) => {
  const [sliderPos, setSliderPos] = useState(50);
  const [isResizing, setIsResizing] = useState(false);
  const { t } = useLanguage();

  const handleMove = (e) => {
    if (!isResizing) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.pageX || e.touches[0].pageX) - rect.left;
    const pos = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPos(pos);
  };

  return (
    <div className="flex flex-col space-y-4">
      <div 
        className="relative aspect-video w-full overflow-hidden rounded-2xl shadow-xl cursor-ew-resize select-none"
        onMouseMove={handleMove}
        onTouchMove={handleMove}
        onMouseDown={() => setIsResizing(true)}
        onMouseUp={() => setIsResizing(false)}
        onMouseLeave={() => setIsResizing(false)}
        onTouchStart={() => setIsResizing(true)}
        onTouchEnd={() => setIsResizing(false)}
      >
        {/* After Image */}
        <img 
          src={after} 
          alt={`${title} ${t({ da: 'efter', en: 'after' })}`}
          className="absolute inset-0 h-full w-full object-cover"
        />
        
        {/* Before Image (Clipped) */}
        <div 
          className="absolute inset-0 h-full w-full overflow-hidden"
          style={{ clipPath: `inset(0 ${100 - sliderPos}% 0 0)` }}
        >
          <img 
            src={before} 
            alt={`${title} ${t({ da: 'før', en: 'before' })}`}
            className="absolute inset-0 h-full w-full object-cover"
          />
        </div>

        {/* Slider Line */}
        <div 
          className="absolute inset-y-0 w-1 bg-white shadow-lg z-10"
          style={{ left: `${sliderPos}%` }}
        >
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-white rounded-full shadow-xl flex items-center justify-center">
            <div className="flex space-x-0.5">
              <ChevronLeft className="w-4 h-4 text-blue-600" />
              <ChevronRight className="w-4 h-4 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Labels */}
        <div className="absolute bottom-4 left-4 z-20 bg-black/50 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs font-medium">
          {t({ da: 'Før', en: 'Before' })}
        </div>
        <div className="absolute bottom-4 right-4 z-20 bg-blue-600/80 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs font-medium">
          {t({ da: 'Efter', en: 'After' })}
        </div>
      </div>
      <div className="px-2">
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
      </div>
    </div>
  );
};

// Original gallery images present before CMS changes
const ORIGINAL_GALLERY_IMAGES = [
  "https://assets.auxodev.dk/assets/c7fe6f8c-4ca5-47b4-ae14-4941efed25b3/890cc588-3dd9-4e3d-8ac3-a759cf7c1aac.jpg",
  "https://assets.auxodev.dk/assets/c7fe6f8c-4ca5-47b4-ae14-4941efed25b3/eed46e66-b74a-4ce7-a725-0425f64c0d76.jpg",
  "https://assets.auxodev.dk/assets/c7fe6f8c-4ca5-47b4-ae14-4941efed25b3/b5cbb00e-21cd-4543-b1b7-68fda6d7c083.jpg",
  "https://assets.auxodev.dk/assets/c7fe6f8c-4ca5-47b4-ae14-4941efed25b3/143821d7-ac1a-4ff8-979b-40d242c28144.jpg",
  "https://assets.auxodev.dk/assets/c7fe6f8c-4ca5-47b4-ae14-4941efed25b3/9969dbe2-354a-4bee-b054-c279df4b666a.jpg",
  "https://assets.auxodev.dk/assets/c7fe6f8c-4ca5-47b4-ae14-4941efed25b3/dbb7d3ec-046d-4a66-be43-98491a04ddb0.jpg",
  "https://assets.auxodev.dk/assets/c7fe6f8c-4ca5-47b4-ae14-4941efed25b3/a0e3f31b-24e4-4c0c-a190-27912e0621b3.jpg",
  "https://assets.auxodev.dk/assets/c7fe6f8c-4ca5-47b4-ae14-4941efed25b3/14d16a07-682c-45e0-bfb4-8e5c6ed6ef0b.jpg",
  "https://assets.auxodev.dk/assets/c7fe6f8c-4ca5-47b4-ae14-4941efed25b3/00633b08-d50f-40ab-94b2-be50e8f2e652.jpg",
  "https://assets.auxodev.dk/assets/c7fe6f8c-4ca5-47b4-ae14-4941efed25b3/56a96a45-b278-447c-8744-9869a6e7726a.jpg",
  "https://assets.auxodev.dk/assets/c7fe6f8c-4ca5-47b4-ae14-4941efed25b3/77f47e48-658c-4e85-aa9b-453c63d99e32.jpg",
  "https://assets.auxodev.dk/assets/c7fe6f8c-4ca5-47b4-ae14-4941efed25b3/5b2b095e-8575-4a4d-9afc-4d6d6bfbbd32.jpg",
  "https://assets.auxodev.dk/assets/c7fe6f8c-4ca5-47b4-ae14-4941efed25b3/92583d6a-44e3-42ae-8d64-98ab6834387e.jpg",
  "https://assets.auxodev.dk/assets/c7fe6f8c-4ca5-47b4-ae14-4941efed25b3/b1b03ecd-76d2-48ba-8b85-7acfb3fb50d1.jpg",
  "https://assets.auxodev.dk/assets/c7fe6f8c-4ca5-47b4-ae14-4941efed25b3/a63c2e08-60a9-402c-878f-267f7d606ec8.jpg",
  "https://assets.auxodev.dk/assets/c7fe6f8c-4ca5-47b4-ae14-4941efed25b3/367b6901-c5ed-40e3-ae4a-7cabcf3ebbe3.jpg",
  "https://assets.auxodev.dk/assets/c7fe6f8c-4ca5-47b4-ae14-4941efed25b3/046a8b7f-ad89-48dd-a2c6-72957891b470.jpg",
  "https://assets.auxodev.dk/assets/c7fe6f8c-4ca5-47b4-ae14-4941efed25b3/2bb99372-3b31-4773-814e-10fe6893e69d.jpg",
  "https://assets.auxodev.dk/assets/c7fe6f8c-4ca5-47b4-ae14-4941efed25b3/b963b545-b288-41a9-b04f-b842e3bcc711.jpg",
  "https://assets.auxodev.dk/assets/c7fe6f8c-4ca5-47b4-ae14-4941efed25b3/5e67416c-a33a-4648-9198-f873d7d128b5.jpg",
  "https://assets.auxodev.dk/assets/c7fe6f8c-4ca5-47b4-ae14-4941efed25b3/81ec4234-1fed-4107-bb4b-ca8580472be5.jpg",
  "https://assets.auxodev.dk/assets/c7fe6f8c-4ca5-47b4-ae14-4941efed25b3/e79a3933-66e3-4fb7-9483-f96f00a21cca.jpg",
  "https://assets.auxodev.dk/assets/c7fe6f8c-4ca5-47b4-ae14-4941efed25b3/e88e6891-88f8-4f52-8040-d20c03f54395.jpg",
  "https://assets.auxodev.dk/assets/c7fe6f8c-4ca5-47b4-ae14-4941efed25b3/2cd80336-efdd-42f6-ac1d-0dc3c3a71d0f.jpg",
  "https://assets.auxodev.dk/assets/c7fe6f8c-4ca5-47b4-ae14-4941efed25b3/1cec8fa4-3763-4fd6-946b-5fd0a5ce0ba3.jpg",
  "https://assets.auxodev.dk/assets/c7fe6f8c-4ca5-47b4-ae14-4941efed25b3/a11ad925-07d6-42be-8a93-ae8f303e195a.jpg",
  "https://assets.auxodev.dk/assets/c7fe6f8c-4ca5-47b4-ae14-4941efed25b3/efe71c97-8248-4e06-93d2-40f6f0884e70.jpg",
  "https://assets.auxodev.dk/assets/c7fe6f8c-4ca5-47b4-ae14-4941efed25b3/ada86ad2-aa91-4e9a-aba2-ab5cb6538fac.jpg"
];

const Gallery = () => {
  const { t } = useLanguage();
  const [items, setItems] = useState(ORIGINAL_GALLERY_IMAGES.map((url, i) => ({ url, title: `${t({ da: 'Billede', en: 'Image' })} ${i + 1}` })));
  const [loading, setLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const { getImage, getText } = useCmsImages();

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        // Fetch from galleri_side in the Danish CMS
        const response = await fetch('https://api.dev.auxo.dk/public/content/galleri_side');
        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0].galleri_billeder) && data[0].galleri_billeder.length > 0) {
            setItems(data[0].galleri_billeder.map((url, i) => ({
              url,
              title: `${t({ da: 'Billede', en: 'Image' })} ${i + 1}`
            })));
            return;
          }
        }
        
        // Fallback to static original images
        setItems(ORIGINAL_GALLERY_IMAGES.map((url, i) => ({ url, title: `${t({ da: 'Billede', en: 'Image' })} ${i + 1}` })));
      } catch (error) {
        console.error('Error fetching gallery:', error);
        setItems(ORIGINAL_GALLERY_IMAGES.map((url, i) => ({ url, title: `${t({ da: 'Billede', en: 'Image' })} ${i + 1}` })));
      } finally {
        setLoading(false);
      }
    };

    fetchGallery();
  }, [t]);

  const handleNext = useCallback(() => {
    if (selectedIndex === null) return;
    setSelectedIndex((prev) => (prev + 1) % items.length);
  }, [selectedIndex, items.length]);

  const handlePrev = useCallback(() => {
    if (selectedIndex === null) return;
    setSelectedIndex((prev) => (prev - 1 + items.length) % items.length);
  }, [selectedIndex, items.length]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedIndex === null) return;
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'Escape') setSelectedIndex(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIndex, handleNext, handlePrev]);

  useEffect(() => {
    document.title = t({ da: "FTR Service ApS - Galleri", en: "FTR Service ApS - Gallery" });
  }, [t]);

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-20 bg-gray-900 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img 
            src={getImage('gallery_hero', '/images/hero-cleaning.png', { width: 1400 })} 
            alt={t({ da: "Baggrund", en: "Background" })} 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center space-x-2 bg-blue-600/20 text-blue-400 px-4 py-2 rounded-full mb-6">
              <Camera className="w-4 h-4" />
              <span className="text-sm font-semibold uppercase tracking-wider">
                {getText('galleri_hero', 'underoverskrift', t({ da: "Vores Resultater", en: "Our Results" }))}
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-6">
              {getText('galleri_hero', 'overskrift', t({ da: "Se Forskellen med FTR Service", en: "See the Difference with FTR Service" }))}
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {getText('galleri_hero', 'tekst', t({ 
                da: "Vi sætter en ære i vores arbejde. Her kan du se et udvalg af vores arbejde, der viser den grundighed og kvalitet, vi leverer til hver eneste opgave.",
                en: "We take pride in our work. Here you can see a selection of our work, showing the thoroughness and quality we deliver to every single task."
              }))}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : items.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {items.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: (index % 6) * 0.1 }}
                  onClick={() => setSelectedIndex(index)}
                  className="group relative aspect-square overflow-hidden rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer bg-gray-100"
                >
                  <img 
                    src={item.url} 
                    alt={item.title || t({ da: `Galleri billede ${index + 1}`, en: `Gallery image ${index + 1}` })}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                    <div className="w-10 h-10 rounded-full bg-white/80 text-gray-900 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-lg">
                      <Camera className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-gray-500 text-lg">{t({ da: "Ingen billeder fundet i galleriet endnu.", en: "No images found in the gallery yet." })}</p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <Dialog.Root open={selectedIndex !== null} onOpenChange={(open) => !open && setSelectedIndex(null)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[100] animate-in fade-in duration-300" />
          <Dialog.Content className="fixed inset-0 z-[101] flex items-center justify-center p-4 sm:p-8 outline-none">
            <VisuallyHidden.Root>
              <Dialog.Title>
                {selectedIndex !== null ? items[selectedIndex].title || t({ da: 'Galleri billede', en: 'Gallery image' }) : 'Gallery'}
              </Dialog.Title>
            </VisuallyHidden.Root>
            <div className="relative w-full max-w-5xl h-full flex items-center justify-center">
              <AnimatePresence mode="wait">
                {selectedIndex !== null && (
                  <motion.div
                    key={selectedIndex}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.3 }}
                    className="relative max-h-full w-full flex flex-col items-center"
                  >
                    <img
                      src={items[selectedIndex].url}
                      alt={items[selectedIndex].title || t({ da: 'Galleri billede', en: 'Gallery image' })}
                      className="max-h-[80vh] w-auto object-contain rounded-lg shadow-2xl"
                    />
                    {items[selectedIndex].description && (
                      <div className="mt-4 text-center">
                        <p className="text-white text-lg font-medium">{items[selectedIndex].description}</p>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Navigation Controls */}
              <button
                onClick={(e) => { e.stopPropagation(); handlePrev(); }}
                className="absolute left-0 top-1/2 -translate-y-1/2 p-2 text-white/50 hover:text-white transition-colors bg-black/20 hover:bg-black/40 rounded-full"
                aria-label={t({ da: "Forrige billede", en: "Previous image" })}
              >
                <ChevronLeft className="w-8 h-8 sm:w-12 sm:h-12" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); handleNext(); }}
                className="absolute right-0 top-1/2 -translate-y-1/2 p-2 text-white/50 hover:text-white transition-colors bg-black/20 hover:bg-black/40 rounded-full"
                aria-label={t({ da: "Næste billede", en: "Next image" })}
              >
                <ChevronRight className="w-8 h-8 sm:w-12 sm:h-12" />
              </button>

              {/* Close Button */}
              <Dialog.Close className="absolute top-0 right-0 p-2 text-white/50 hover:text-white transition-colors bg-black/20 hover:bg-black/40 rounded-full">
                <X className="w-6 h-6 sm:w-8 h-8" />
              </Dialog.Close>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* CTA Section */}
      <section className="bg-blue-600 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-8">
            {t({ 
              da: "Skal vi også gøre din bolig eller virksomhed skinnende ren?",
              en: "Should we also make your home or business sparkling clean?"
            })}
          </h2>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link 
              to="/kontakt" 
              className="inline-flex items-center justify-center px-8 py-4 border border-transparent text-lg font-bold rounded-xl text-blue-600 bg-white hover:bg-gray-50 transition-all shadow-lg"
            >
              {t({ da: "Få et gratis tilbud", en: "Get a free quote" })}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <a 
              href="tel:+4591847666" 
              className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-lg font-bold rounded-xl text-white hover:bg-white/10 transition-all"
            >
              {t({ da: "Ring til os nu", en: "Call us now" })}
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Gallery;
